<!-- eslint-disable max-len -->
<template>
  <div class="container py-3">
    <div class="breadcrumb-voucher py-3">
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link to="/">
              <Icon class="color--info" name="house" />
            </router-link>
          </li>
          <li class="breadcrumb-item">
            <router-link class="color--info" to="/">
              Emissão de Certificado
            </router-link>
          </li>
          <li class="breadcrumb-item">
            <router-link class="color--primary disabled fw-600" to="/">
              Certificado SSL
            </router-link>
          </li>
        </ol>
      </nav>
    </div>

    <Wizzard current-page="alldone" />

    <h2 class="w-100 fw-600 text-start mt-5">
      Pra terminar, vamos validar seu domínio
    </h2>

    <div class="row pt-5">
      <div class="d-none d-lg-block col-lg-5 d-flex justify-content-center">
        <img
          id="domain-validation-image"
          src="@/assets/images/domain-validation.svg"
          alt="domain-validation"
        />
      </div>
      <div class="col-12 col-lg-7 d-flex flex-column justify-content-start">
        <p class="text-start fs-6 mb-4">
          Escolha um tipo de validação para prosseguirmos com a emissão do seu
          Certificado Digital SSL.
        </p>

        <div class="mb-5">
          <p class="w-100 text-start fs-6 mb-2">
            Escolha um tipo de validação *
          </p>
          <Selectable
            v-model="validationMethod"
            icon="lock"
            placeholder="Selecione uma opção"
            :options="validationMethodOptions"
          />
        </div>

        <div>
          <p class="w-100 text-start fs-6 mb-2">
            Escolha o domínio que deseja validar *
          </p>
          <Selectable
            v-model="domain"
            icon="globe"
            placeholder="Selecione uma opção"
            :options="domainOptions"
            :is-disabled="!domainsAreLoaded"
          />
        </div>

        <div class="my-5 d-flex justify-content-end">
          <button
            :class="[
              'progress-button btn btn-primary',
              // eslint-disable-next-line max-len
              !validationMethodIsSelected || !domainIsSelected
                ? 'btn-secondary disabled'
                : 'btn-primary',
            ]"
            :disabled="!validationMethodIsSelected || !domainIsSelected"
            @click="handleNextStep"
          >
            <span class="me-2">Avançar</span>
            <Icon name="arrow-right" />
          </button>
        </div>
      </div>
    </div>

    <Contact />

    <ModalEmailValidation
      ref="modalEmailValidation"
      :selected-domain="domain"
      :error="errorCode"
      @validate-email="handleValidateEmail"
      @remove-error-log="errorCode = ''"
    />
    <button
      v-show="false"
      id="modal-email-validation-button"
      type="button"
      data-bs-toggle="modal"
      data-bs-target="#modal-email-validation"
    />

    <ModalOnlyUseOnDesktop>
      <template #message>
        <h3>
          A validação de seu domínio através de Requisção HTTP, deve ser feita
          em um
          <strong>computador ou notebook.</strong>
        </h3>
      </template>
    </ModalOnlyUseOnDesktop>
    <button
      v-show="false"
      id="modal-only-desktop-issuer-button"
      data-bs-target="#modal-only-desktop"
      data-bs-toggle="modal"
    />
  </div>

  <Loading :is-loading="isLoading" />
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import Icon from '@/components/Atoms/Icon.vue'
  import Wizzard from '@/components/Molecules/Wizzard.vue'
  import Contact from '@/components/Molecules/Contact.vue'
  import Selectable from '@/components/Atoms/Selectable.vue'
  import { AxiosError } from 'axios'
  import { useModalLoading } from '@/store/Pinia/useModalLoading.store'
  import Loading from '@/components/Atoms/Loading.vue'
  import { mapActions, mapState } from 'pinia'
  import { useDynamicForms } from '@/store/Pinia/useDynamicForms.store'
  import { useSolicitation } from '@/store/Pinia/useSolicitation.store'
  import { useToastGlobal } from '@/store/Pinia/useToastGlobal.store'
  import { useDomainValidation } from '@/store/Pinia/useDomainValidation.store'
  import browserDeviceCheck from '@/utils/browserDeviceCheck'
  import { domainValidationController } from '@/infra/DomainValidation/domainValidation.controller'
  import emailValidationController from '@/infra/DomainValidation/email.controller'
  import httpValidationController from '@/infra/DomainValidation/http.controller'
  import dnsValidationController from '@/infra/DomainValidation/dns.controller'
  import ModalOnlyUseOnDesktop from '@/components/templates/ModalOnlyUseOnDesktop.vue'
  import ModalEmailValidation from './email/ModalEmailValidation.vue'

  interface SelectableOption {
    id: number
    label: string
    value: string
    nome: string
  }

  export default defineComponent({
    components: {
      Icon,
      Wizzard,
      Contact,
      Loading,
      Selectable,
      ModalEmailValidation,
      ModalOnlyUseOnDesktop,
    },
    data() {
      return {
        isLoading: false,
        isMobile: false,
        validationMethodOptions: [
          {
            id: 1,
            label: 'E-mail',
            value: 'email',
            nome: 'email',
          },
          {
            id: 2,
            label: 'Requisição HTTP',
            value: 'http',
            nome: 'http',
          },
          {
            id: 3,
            label: 'Registro CNAME no DNS',
            value: 'dns',
            nome: 'dns',
          },
        ],
        validationMethod: '',
        domainOptions: [] as SelectableOption[],
        domain: '',
        errorCode: '',
      }
    },
    watch: {
      validationMethod() {
        this.getDomains()
      },
    },
    computed: {
      ...mapState(useDynamicForms, ['getPassword']),
      ...mapState(useSolicitation, ['getSolicitation']),
      validationMethodIsSelected() {
        if (this.validationMethod) {
          return true
        }
        return false
      },
      domainsAreLoaded() {
        if (this.domainOptions.length > 0) {
          return true
        }
        return false
      },
      domainIsSelected() {
        if (this.domain) {
          return true
        }
        return false
      },
    },
    methods: {
      ...mapActions(useModalLoading, ['setIsLoading']),
      ...mapActions(useToastGlobal, ['toggleToastGlobal']),
      ...mapActions(useDomainValidation, [
        'setDomainHttpValidationData',
        'setDomainDnsValidationData',
      ]),
      async getDomains() {
        try {
          this.setIsLoading({
            isLoading: true,
            title: 'Aguarde enquanto listamos seus domínios.',
            message: '',
            image: 'clock',
          })

          const response = await domainValidationController.getDomains(
            this.getSolicitation.cdSolicitacao,
            this.getPassword,
          )

          this.domainOptions = this.mapDomainsToOptions(response.domains)
        } catch (_error) {
          this.toggleToastGlobal({
            isShow: true,
            type: 'danger',
            message:
              'Houve um erro com a requisição, por favor tente novamente mais tarde.',
          })
        } finally {
          setTimeout(() => {
            this.setIsLoading({ isLoading: false })
          }, 1000)
        }
      },
      mapDomainsToOptions(domains: string[]) {
        return domains.map((domain, index) => ({
          id: index,
          label: domain,
          nome: domain,
          value: domain,
        }))
      },
      async handleNextStep() {
        if (this.validationMethod === 'email') {
          return this.handleEmailValidationSelected()
        }

        if (this.validationMethod === 'http') {
          return this.handleHttpValidationSelected()
        }

        if (this.validationMethod === 'dns') {
          return this.handleDnsValidationSelected()
        }

        this.toggleToastGlobal({
          isShow: true,
          type: 'danger',
          message: 'Selecione um método de validação válido.',
        })
      },
      async handleEmailValidationSelected() {
        try {
          this.setIsLoading({
            isLoading: true,
            title: 'Aguarde enquanto buscamos seus e-mails.',
            message: '',
            image: 'clock',
          })

          // eslint-disable-next-line max-len
          await (
            this.$refs.modalEmailValidation as InstanceType<
              typeof ModalEmailValidation
            >
          ).fetchEmails()

          if (this.validationMethod === 'email') {
            ;(
              document.getElementById(
                'modal-email-validation-button',
              ) as HTMLElement
            ).click()
          }
        } catch (_error) {
          this.toggleToastGlobal({
            isShow: true,
            type: 'danger',
            message:
              'Houve um erro com a requisição, por favor tente novamente mais tarde.',
          })
        } finally {
          setTimeout(() => {
            this.setIsLoading({ isLoading: false })
          }, 1000)
        }
      },
      async handleHttpValidationSelected() {
        if (this.isMobile) {
          return (
            document.getElementById(
              'modal-only-desktop-issuer-button',
            ) as HTMLButtonElement
          ).click()
        }

        try {
          this.setIsLoading({
            isLoading: true,
            title: 'Aguarde enquanto selecionamos o método de validação.',
            message: '',
            image: 'clock',
          })

          const response = await httpValidationController.choseMethod({
            codigo: this.getSolicitation.cdSolicitacao,
            senha: this.getPassword,
            domain: this.domain,
          })

          this.setDomainHttpValidationData(response.choosedValidationData[0])
          return this.$router.push({
            path: '/domain-validation/http-instructions',
          })
        } catch (_error) {
          this.toggleToastGlobal({
            isShow: true,
            type: 'info',
            message: 'O domínio selecionado já foi validado.',
          })
          return null
        } finally {
          setTimeout(() => {
            this.setIsLoading({ isLoading: false })
          }, 1000)
        }
      },
      async handleDnsValidationSelected() {
        try {
          this.setIsLoading({
            isLoading: true,
            title: 'Aguarde enquanto selecionamos o método de validação.',
            message: '',
            image: 'clock',
          })

          const response = await dnsValidationController.choseMethod({
            codigo: this.getSolicitation.cdSolicitacao,
            senha: this.getPassword,
            domain: this.domain,
          })

          this.setDomainDnsValidationData(response.choosedValidationData[0])
          return this.$router.push({
            path: '/domain-validation/dns-instructions',
          })
        } catch (_error) {
          this.toggleToastGlobal({
            isShow: true,
            type: 'info',
            message: 'O domínio selecionado já foi validado.',
          })
          return null
        } finally {
          setTimeout(() => {
            this.setIsLoading({ isLoading: false })
          }, 1000)
        }
      },
      async handleValidateEmail(code: string) {
        try {
          this.isLoading = true

          await emailValidationController.validateCode({
            codigo: this.getSolicitation.cdSolicitacao,
            senha: this.getPassword,
            domain: this.domain,
            code,
          })

          const modal = this.$refs
            .modalEmailValidation as typeof ModalEmailValidation

          modal.modalClose()

          if (!this.getSolicitation.isQsa) {
            this.$router.push({
              path: '/domain-validation/validation-success/feedback-http-email-dns-ontime-wo-qsa',
            })
            modal.modalClose()
            return
          }

          modal.modalClose()
          this.$router.push({
            path: '/ssl-certificate/installation-guide-ssl',
          })
          return
        } catch (_error) {
          const err = _error as AxiosError<{ message: string }>
          if (err.response?.data.message === 'Código de Validação inválido.') {
            this.errorCode = 'Oops! Código inválido.'
          } else {
            this.toggleToastGlobal({
              isShow: true,
              type: 'danger',
              message:
                'Houve um erro com a requisição, por favor tente novamente mais tarde.',
            })
          }
        } finally {
          this.isLoading = false
        }
      },
    },
    mounted() {
      this.isMobile = browserDeviceCheck()
    },
    beforeRouteEnter(to, from, next) {
      const solicitationStore = useSolicitation()
      if (solicitationStore.getSolicitation.cdSolicitacao === '') {
        return next('/')
      }
      return next()
    },
  })
</script>

<style scoped>
  #domain-validation-image {
    width: auto;
    height: 380px;
  }

  @media screen and (max-width: 992px) {
    .progress-button {
      width: 100%;
    }
  }
</style>
